import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  ParamMap,
  Router,
  RouterEvent,
  UrlSegment
} from '@angular/router';
import {filter, mergeMap, switchMap, tap} from 'rxjs/operators';
import {CallanOnlineRecognizeService} from "../../services/callan-online-recognize.service";
import {combineLatest, Observable, Subscription} from "rxjs";
import {VerifiedEmailChangeRequest, VerifiedPasswordChangeRequest} from "../../model/v3/people";
import {PersonPasswordChangeComponent} from "../../components/person-password-change/person-password-change.component";
import {PersonEmailChangeComponent} from "../../components/person-email-change/person-email-change.component";
import {ToastNotification, ToastNotificationsService} from "../../services/toast-notifications.service";
import {ConfirmDialogService} from "../../services/confirm-dialog.service";
import {ManagerUtils} from 'src/app/services/utils/v3/manager-utils';
import {FunctionalAccessService} from "../../services/functional-access.service";
import {StudentV3RestService} from '../../services/rest/v3/student-rest.service';
import {WorkflowsRestService} from '../../services/rest/v3/workflows-rest.service';
import {SchoolStudentStatus, V3Student} from '../../model/v3/schools';
import {PeopleV3RestService} from "../../services/rest/v3/people-rest.service";
import {CasaRestApiV2} from "../../services/rest/casa-rest.service";

import {ManagerSubscriptionsGuardService} from "../../services/col2/manager-subscriptions-guard.service";
@Component({
    selector: 'app-manager-student-details-layout',
    templateUrl: './manager-student-details-layout.component.html',
    styleUrls: ['./manager-student-details-layout.component.css'],
    standalone: false
})
export class ManagerStudentDetailsLayoutComponent implements OnInit, OnDestroy {
  schoolId: number;
  studentId: number;
  student: V3Student;
  currentFragment: string;
  private currentFragments: string[];
  searchTerm = "";
  @ViewChild('passwordModal', {static: true})
  passwordModal: PersonPasswordChangeComponent;
  @ViewChild('emailModal', {static: true})
  emailModal: PersonEmailChangeComponent;
  private dataSubscription: Subscription

  constructor(
    private activeRoute: ActivatedRoute,
    private router: Router,
    private casa: CasaRestApiV2,
    private studentV3Rest: StudentV3RestService,
    private peopleV3Rest: PeopleV3RestService,
    private workflowsRest: WorkflowsRestService,
    public callanOnlineRecognize: CallanOnlineRecognizeService,
    private confirmationService: ConfirmDialogService,
    private toastNotificationService: ToastNotificationsService,
    public functionalAccessService: FunctionalAccessService,
    public subscriptionsGuard: ManagerSubscriptionsGuardService) {
      router.events.pipe(
        filter((e: Event): e is NavigationStart | NavigationEnd | NavigationCancel | NavigationError => e instanceof NavigationStart ||
                                    e instanceof NavigationEnd ||
                                    e instanceof NavigationCancel ||
                                    e instanceof NavigationError))
      .subscribe((e: RouterEvent) => {
          e['urlAfterRedirects']? this.currentFragments = e['urlAfterRedirects'].split('/') : ''
          if (this.currentFragments != null)
          this.currentFragment = this.currentFragments[this.currentFragments.length - 1]
      });
    }


  changePasswordCallback = (request: VerifiedPasswordChangeRequest) => this.studentV3Rest.changeStudentPasswordInstantly(this.schoolId, this.studentId, request)
  changeEmailCallback = (request: VerifiedEmailChangeRequest) => this.workflowsRest.requestStudentEmailChange(this.schoolId, this.studentId, request)

  getPersonName() {
    return this.student ? ManagerUtils.getName(this.student.person) : '';
  }

  getInitials(student: V3Student) {
    return ManagerUtils.getInitials(student.person)
  }


  loadStudent(): Observable<V3Student> {

    return this.studentV3Rest.getStudent(this.schoolId, this.studentId).pipe(
      tap( student => {
        this.student = student;
        const childActiveStateUrl = (this.activeRoute.children && this.activeRoute.children.length>0) ? this.activeRoute.children[0].snapshot.url : null;
        const selectedStudentPage = this.getStudentPageSelected(childActiveStateUrl);
        if (!selectedStudentPage) {
          this.redirectToStudentDefaultPage();
        }
      })
    )
  }
  ngOnInit() {
    this.dataSubscription = combineLatest([
      this.activeRoute.parent.paramMap,
      this.activeRoute.paramMap
    ]).pipe(
      tap<[ParamMap, ParamMap]>(([parentParams, params]) => {
        this.schoolId = Number(parentParams.get('schoolId'));
        this.studentId = Number(params.get('studentId'));
      }),
      mergeMap( _ => this.loadStudent())
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
  }

  private supportedPaths = ['credits', 'lessons', 'profile', 'products', 'subscriptions', 'cspa-monitor', 'casa-profile', 'exam-results'];

  getStudentPageSelected(urls: UrlSegment[]) {
    if (!urls) return null;
    if (urls.length > 0) {
      const lastPath = urls.length > 0 ? urls[urls.length - 1].path : '';
      const prevPath = urls.length > 1 ? urls[urls.length - 2].path : '';

      if (this.supportedPaths.indexOf(lastPath) >=0 ) return lastPath;
      // special case for lessons details
      else if(this.supportedPaths.indexOf(prevPath) >=0) return prevPath;
      else {
        return null;
      }
    }
    return null;
  }

  isCallanOnline() {
    if (!this.schoolId) return false;
    return this.callanOnlineRecognize.isCallanOnlineFranchiseById(this.schoolId);
  }

  private redirectToStudentDefaultPage() {
    if (!this.schoolId || !this.studentId) return;
    if (this.isCallanOnline()) {
      this.router.navigate(["school", this.schoolId.toString(), "student", this.studentId.toString(), "lessons"]).then();
    } else {
      this.router.navigate(["school", this.schoolId.toString(), "student", this.studentId.toString(), "products"]).then();
    }
  }

  changePassword() {
    this.passwordModal.show();
  }

  changeEmail() {
    this.emailModal.show();
  }

  prolongStudent() {
    this.confirmationService.doOnConfirm("Are you sure you want renew the student?",
      () => {
        this.studentV3Rest.updateExpiryDate(this.schoolId, this.studentId, null).pipe(
          switchMap(_ => this.studentV3Rest.updateStatus(this.schoolId, this.studentId, SchoolStudentStatus.ACTIVE)),
          tap(_ => this.toastNotificationService.display(new ToastNotification("Student renewal",`Student access has been renewed.`))),
          switchMap( _ => this.loadStudent())
        ).subscribe()
      } ,
      "Student renewal");
  }

  blockStudent() {
    this.confirmationService.doOnConfirm("Are you sure you want to block the student?",
      () => {
        this.studentV3Rest.updateStatus(this.schoolId, this.studentId, SchoolStudentStatus.INACTIVE).pipe(
          tap(_ => this.toastNotificationService.display(new ToastNotification("Student blocking",`Student has been blocked.`))),
          switchMap( _ =>  this.loadStudent())
        ).subscribe();
      },"Student blocking", "bg-warning");
  }

  deleteStudent() {
    this.confirmationService.doOnConfirm("Are you sure you want delete the student?",
      () => {
        this.studentV3Rest.deleteStudent(this.schoolId, this.studentId).pipe(
          tap(_ => this.toastNotificationService.display(new ToastNotification("Student deletion",`Student has been deleted.`))),
        ).subscribe(_ => this.router.navigate(["school", this.schoolId.toString(), "students"]));
      },
      "Student deletion",
      "bg-danger text-white"
    );
  }

  getStatus() {
    if (!this.student) return '';
    return this.student.status;
  }

  getAccountEmail() {
    if (!this.student) return '';
    return this.student.person.registrationEmail;
  }

  resetBookShares(student: V3Student) {
    this.confirmationService.doOnConfirm("Are you sure you to reset student eBooks download limit?\n" +
      "This will remove the eBooks from all student's devices, so that they can download the eBooks to new devices.",
      () => {
        this.casa.clearStudentDeviceShares(this.schoolId, student.id).subscribe(
          _ => this.toastNotificationService.display(
            new ToastNotification("Reset of student's eBooks download limit", `Students' eBooks download limit reset successfully.`)
          )
        )
      }, "Reset of student's eBooks download limit","bg-warning");
  }

  reloadStudent(student: V3Student) {
    this.casa.syncStudent(this.schoolId, student.id).subscribe(
      _ => this.toastNotificationService.display(
        new ToastNotification("Refreshing of student's eBooks & Exercises", `Students' eBooks & Exercises has been refreshed successfully. Student should be able to access all content.`, "bg-success text-white"))
    )
  }

  anonymize(student: V3Student) {
    this.confirmationService.doOnConfirm("Are you sure you want to permanent delete the student?",
      () => {
        this.peopleV3Rest.deletePerson(student.person.id).subscribe(
           _ => {
              this.toastNotificationService.display(new ToastNotification("Student has been deleted", "Student has been deleted correctly"));
             this.router.navigate(["school", this.schoolId.toString(), "students"]).then();
          }
        )

      }, "Student permanent delete", "bg-danger text-white"
      )
  }
}
